import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import {
  getIntroMode,
  resolveExperienceQuality,
  type ExperiencePreference,
} from '../lib/experience'
import { useScrollLock } from '@se-freshman-digital-2026/shared/useScrollLock'

const QUALITY_KEY = 'se-universe:quality'
const VISITED_KEY = 'se-universe:visited'

export function useExperience() {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)')
  const savedPreference = window.localStorage.getItem(QUALITY_KEY)
  const initialPreference: ExperiencePreference =
    savedPreference === 'auto' ||
    savedPreference === 'high' ||
    savedPreference === 'balanced' ||
    savedPreference === 'lite'
      ? savedPreference
      : 'auto'

  const preference = ref<ExperiencePreference>(initialPreference)
  const prefersReducedMotion = ref(media.matches)
  const width = ref(window.innerWidth)
  const introComplete = ref(false)
  const { lock, unlock } = useScrollLock()

  let finishTimer: number | undefined

  const readSignals = () => {
    width.value = window.innerWidth
    prefersReducedMotion.value = media.matches
  }

  onMounted(() => {
    media.addEventListener('change', readSignals)
    window.addEventListener('resize', readSignals, { passive: true })
  })

  onBeforeUnmount(() => {
    media.removeEventListener('change', readSignals)
    window.removeEventListener('resize', readSignals)
    window.clearTimeout(finishTimer)
    unlock()
  })

  const quality = computed(() =>
    resolveExperienceQuality({
      preference: preference.value,
      reducedMotion: prefersReducedMotion.value,
      viewportWidth: width.value,
      deviceMemory: navigator.deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
    }),
  )

  const introMode = computed(() => {
    const visited = window.sessionStorage.getItem(VISITED_KEY) === 'true'
    return getIntroMode({
      reducedMotion: prefersReducedMotion.value,
      visitedThisSession: visited,
    })
  })

  watch(
    prefersReducedMotion,
    (reduced) => {
      document.documentElement.dataset.motion = reduced ? 'reduced' : 'full'
    },
    { immediate: true },
  )

  watch(preference, (value) => window.localStorage.setItem(QUALITY_KEY, value))

  function scheduleIntro() {
    const delay = introMode.value === 'full' ? 5600 : introMode.value === 'compact' ? 850 : 0
    window.clearTimeout(finishTimer)

    if (introMode.value !== 'instant') {
      lock()
    }

    finishTimer = window.setTimeout(finishIntro, delay)
  }

  function finishIntro() {
    window.clearTimeout(finishTimer)
    introComplete.value = true
    window.sessionStorage.setItem(VISITED_KEY, 'true')
    unlock()
  }

  function cycleQuality() {
    const values: ExperiencePreference[] = ['auto', 'high', 'balanced', 'lite']
    preference.value = values[(values.indexOf(preference.value) + 1) % values.length]
  }

  return {
    preference,
    quality,
    introMode,
    introComplete,
    prefersReducedMotion,
    scheduleIntro,
    finishIntro,
    cycleQuality,
  }
}
